import { primary } from '../../../theme/palette'
import { useMemo, useState } from 'react'
import {
  Box, Button, Card, IconButton, Stack, Tooltip, Typography,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import { PRIORITY_META, type DummyWorkOrder } from '../dummy'
import { useSelectWorkOrder, useWorkOrderRowMenu } from '../WorkOrdersPage'
import { useIsTaskModule } from '../moduleContext'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const PRIORITY_ORDER: Array<DummyWorkOrder['priority']> = ['critical', 'high', 'medium', 'low']

type CalendarRange = 'month' | 'quarter' | 'year'

const RANGE_OPTIONS: Array<{ value: CalendarRange; label: string }> = [
  { value: 'month',   label: 'Month' },
  { value: 'quarter', label: '3-Month' },
  { value: 'year',    label: 'Year' },
]

export function CalendarView({ workOrders }: { workOrders: DummyWorkOrder[] }) {
  const [range, setRange] = useState<CalendarRange>('month')
  const [anchor, setAnchor] = useState(new Date())
  const [selected, setSelected] = useState<Date>(() => new Date())
  const select = useSelectWorkOrder()
  const openRowMenu = useWorkOrderRowMenu()
  const isTask = useIsTaskModule()
  const noun = isTask ? 'task' : 'work order'

  const byDay = useMemo(() => {
    const map = new Map<string, DummyWorkOrder[]>()
    workOrders.forEach((w) => {
      const key = w.dueDate.toDateString()
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(w)
    })
    return map
  }, [workOrders])

  // Step size follows whatever range is active — a month at a time in
  // Month view, a 3-month block in 3-Month view, a full year in Year
  // view. Year view always re-derives Jan–Dec of `anchor`'s year (see
  // monthsToRender), so stepping the year is what actually moves it.
  function shift(delta: number) {
    const next = new Date(anchor)
    if (range === 'month') next.setMonth(next.getMonth() + delta)
    else if (range === 'quarter') next.setMonth(next.getMonth() + delta * 3)
    else next.setFullYear(next.getFullYear() + delta)
    setAnchor(next)
  }

  function goToday() {
    const t = new Date()
    setAnchor(t)
    setSelected(t)
  }

  // Drill-down from a mini-month's title in 3-Month/Year view straight
  // into Month view anchored on that month — cheap, expected affordance
  // once you're staring at 12 tiny months and want a closer look.
  function jumpToMonth(monthAnchor: Date) {
    setAnchor(monthAnchor)
    setRange('month')
  }

  const monthsToRender = useMemo(() => {
    if (range === 'month') return [anchor]
    if (range === 'quarter') {
      return [0, 1, 2].map((i) => new Date(anchor.getFullYear(), anchor.getMonth() + i, 1))
    }
    return Array.from({ length: 12 }, (_, i) => new Date(anchor.getFullYear(), i, 1))
  }, [anchor, range])

  const title = useMemo(() => {
    if (range === 'month') return anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    if (range === 'year') return String(anchor.getFullYear())
    const start = monthsToRender[0]!
    const end = monthsToRender[monthsToRender.length - 1]!
    const sameYear = start.getFullYear() === end.getFullYear()
    const startLabel = start.toLocaleDateString(undefined, sameYear ? { month: 'short' } : { month: 'short', year: 'numeric' })
    const endLabel = end.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
    return `${startLabel} – ${endLabel}`
  }, [anchor, range, monthsToRender])

  const dayItems = byDay.get(selected.toDateString()) ?? []
  const hasAnyPriorityData = workOrders.length > 0

  return (
    <Card
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        overflow: 'hidden',
      }}
    >
      {/* ─── Calendar grid ─── */}
      <Box
        sx={{
          flex: { xs: 'initial', md: 1 },
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          borderRight: { xs: 'none', md: '1px solid' },
          borderBottom: { xs: '1px solid', md: 'none' },
          borderColor: 'divider',
        }}
      >
        {/* Top bar */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          rowGap={1}
          sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.015em' }}>
            {title}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <RangeSwitcher value={range} onChange={setRange} />
            <Stack direction="row" alignItems="center" spacing={0.25}>
              <IconButton size="small" onClick={() => shift(-1)} sx={{ color: 'text.secondary' }}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Button
                size="small"
                onClick={goToday}
                sx={{ fontSize: 12, color: 'text.secondary', minWidth: 0, px: 1.25 }}
              >
                Today
              </Button>
              <IconButton size="small" onClick={() => shift(1)} sx={{ color: 'text.secondary' }}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </Stack>

        {/* Month grid(s) — one large grid in Month view, several
            condensed grids side by side in 3-Month/Year view. Month
            view stays a flex column (weekday row + day grid stacked,
            day grid stretching to fill) — MonthGrid renders as
            `display: contents` for size="lg" so its children join
            this flex chain directly, same as before the refactor. */}
        <Box sx={{
          flex: 1, minHeight: 0,
          overflowY: range === 'month' ? 'hidden' : 'auto',
          p: range === 'month' ? 0 : 2,
          ...(range === 'month' ? { display: 'flex', flexDirection: 'column' } : {}),
        }}>
          {range === 'month' ? (
            <MonthGrid
              monthAnchor={anchor}
              size="lg"
              byDay={byDay}
              selected={selected}
              onSelectDay={setSelected}
              noun={noun}
            />
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(3, 1fr)',
                  lg: range === 'quarter' ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)',
                },
                gap: 2.5,
              }}
            >
              {monthsToRender.map((m) => (
                <MonthGrid
                  key={m.toISOString()}
                  monthAnchor={m}
                  size={range === 'quarter' ? 'md' : 'sm'}
                  byDay={byDay}
                  selected={selected}
                  onSelectDay={setSelected}
                  onTitleClick={() => jumpToMonth(m)}
                  noun={noun}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Priority legend — explains the density bars/dots on the grid.
            Only worth the footprint once there's actual data to explain. */}
        {hasAnyPriorityData && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            flexWrap="wrap"
            sx={{ px: 3, py: 1.25, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}
          >
            {PRIORITY_ORDER.map((p) => {
              const meta = PRIORITY_META[p] ?? { label: p, color: '#D97706' }
              return (
                <Stack key={p} direction="row" alignItems="center" spacing={0.6}>
                  <Box sx={{ width: 7, height: 7, borderRadius: 99, bgcolor: meta.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>
                    {meta.label}
                  </Typography>
                </Stack>
              )
            })}
          </Stack>
        )}
      </Box>

      {/* ─── Day agenda panel ─── */}
      <Box
        sx={{
          width: { xs: '100%', md: 340 },
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          bgcolor: (t) => (t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.015)'),
        }}
      >
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'text.secondary',
              mb: 0.25,
            }}
          >
            {selected.toLocaleDateString(undefined, { weekday: 'long' })}
          </Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.1 }}>
            {selected.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
            {dayItems.length === 0
              ? 'Nothing scheduled'
              : `${dayItems.length} ${noun}${dayItems.length === 1 ? '' : 's'}`}
          </Typography>
        </Box>

        <Stack
          spacing={1}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            px: 2, py: 2,
          }}
        >
          {dayItems.length === 0 ? (
            <Box
              sx={{
                py: 5, textAlign: 'center',
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                No {noun}s on this day.
              </Typography>
            </Box>
          ) : (
            dayItems
              .sort((a, b) => priorityRank(b.priority) - priorityRank(a.priority))
              .map((wo) => (
                <AgendaItem key={wo.id} wo={wo}
                  onClick={() => select(wo)}
                  onContextMenu={(e) => openRowMenu(e, wo)} />
              ))
          )}
        </Stack>
      </Box>
    </Card>
  )
}

// ─── Range switcher (Month / 3-Month / Year) ───────────────────────────────

function RangeSwitcher({ value, onChange }: { value: CalendarRange; onChange: (v: CalendarRange) => void }) {
  return (
    <Stack direction="row" spacing={0.25} sx={{
      display: 'inline-flex', p: 0.35, borderRadius: 1.5,
      bgcolor: 'action.hover',
    }}>
      {RANGE_OPTIONS.map((opt) => {
        const active = value === opt.value
        return (
          <Box
            key={opt.value}
            component="button"
            onClick={() => onChange(opt.value)}
            sx={{
              border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              px: 1.25, py: 0.5, borderRadius: 1,
              fontSize: 12, fontWeight: 600, letterSpacing: '-0.005em',
              transition: 'background-color 120ms ease, color 120ms ease',
              bgcolor: active ? 'background.paper' : 'transparent',
              color: active ? 'primary.main' : 'text.secondary',
              boxShadow: active ? '0 1px 2px rgba(15,23,42,0.08)' : 'none',
              '&:hover': !active ? { color: 'text.primary' } : undefined,
            }}
          >
            {opt.label}
          </Box>
        )
      })}
    </Stack>
  )
}

// ─── Month grid (shared by Month / 3-Month / Year views) ──────────────────

type GridSize = 'lg' | 'md' | 'sm'

const SIZE_CFG: Record<GridSize, {
  badge: number; badgeFont: number; cellPadX: number; cellPadY: number
  gap: number; showCount: boolean; showBars: boolean; showDot: boolean
  showMonthTitle: boolean; weekdayFull: boolean
}> = {
  lg: { badge: 22, badgeFont: 12, cellPadX: 1,    cellPadY: 0.75, gap: 0.5,   showCount: true,  showBars: true,  showDot: false, showMonthTitle: false, weekdayFull: true },
  md: { badge: 19, badgeFont: 11, cellPadX: 0.5,  cellPadY: 0.4,  gap: 0.375, showCount: true,  showBars: true,  showDot: false, showMonthTitle: true,  weekdayFull: true },
  sm: { badge: 17, badgeFont: 10, cellPadX: 0.25, cellPadY: 0.25, gap: 0.25,  showCount: false, showBars: false, showDot: true,  showMonthTitle: true,  weekdayFull: false },
}

function MonthGrid({ monthAnchor, size, byDay, selected, onSelectDay, onTitleClick, noun }: {
  monthAnchor: Date
  size: GridSize
  byDay: Map<string, DummyWorkOrder[]>
  selected: Date
  onSelectDay: (d: Date) => void
  onTitleClick?: () => void
  noun: string
}) {
  const days = useMemo(() => buildMonthGrid(monthAnchor), [monthAnchor])
  const cfg = SIZE_CFG[size]

  return (
    // `lg` (Month view) uses `display: contents` so its weekday-row and
    // day-grid children become direct flex children of the parent flex
    // column in CalendarView — needed for the day grid's `flex: 1` to
    // actually stretch and fill the available height. `md`/`sm` render
    // as a normal block box, since those sit inside a CSS grid of
    // several mini-months and just size to content.
    <Box sx={size === 'lg' ? { display: 'contents' } : undefined}>
      {cfg.showMonthTitle && (
        <Typography
          onClick={onTitleClick}
          sx={{
            fontSize: size === 'md' ? 13 : 12, fontWeight: 700, letterSpacing: '-0.01em',
            mb: 0.75, cursor: onTitleClick ? 'pointer' : 'default',
            color: 'text.primary',
            '&:hover': onTitleClick ? { color: 'primary.main' } : undefined,
          }}
        >
          {monthAnchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
        </Typography>
      )}

      {/* Weekday row */}
      <Box sx={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
        ...(size === 'lg'
          ? { px: 1.5, pt: 1.5, pb: 0.5, flexShrink: 0 }
          : { pb: 0.4 }),
      }}>
        {WEEKDAYS.map((d) => (
          <Typography
            key={d}
            sx={{
              fontSize: size === 'lg' ? 10.5 : 9,
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              textAlign: 'center',
            }}
          >
            {cfg.weekdayFull ? d : d[0]}
          </Typography>
        ))}
      </Box>

      {/* Day cells */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gridTemplateRows: 'repeat(6, 1fr)',
          gap: cfg.gap,
          ...(size === 'lg' ? { flex: 1, minHeight: 0, px: 1.5, pb: 1.5 } : {}),
        }}
      >
        {days.map((d) => {
          const items = byDay.get(d.toDateString()) ?? []
          const isCurrentMonth = d.getMonth() === monthAnchor.getMonth()
          const isToday = sameDay(d, new Date())
          const isSelected = sameDay(d, selected)
          const counts = countByPriority(items)
          const topPriority = counts[0]?.priority

          const cell = (
            <Box
              onClick={() => onSelectDay(d)}
              sx={{
                position: 'relative',
                borderRadius: size === 'lg' ? 1.25 : 1,
                px: cfg.cellPadX, py: cfg.cellPadY,
                cursor: 'pointer',
                opacity: isCurrentMonth ? 1 : 0.35,
                bgcolor: isSelected ? primary.highlight : 'transparent',
                border: '1px solid',
                borderColor: isSelected ? 'primary.main' : 'transparent',
                transition: 'background-color 120ms ease, border-color 120ms ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: size === 'lg' ? 'stretch' : 'center',
                minHeight: 0,
                '&:hover': {
                  bgcolor: isSelected
                    ? primary.highlightHover
                    : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)',
                },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent={size === 'lg' ? 'space-between' : 'center'}
                sx={{ width: '100%' }}
              >
                <Box
                  sx={{
                    minWidth: cfg.badge, height: cfg.badge,
                    px: size === 'lg' ? 0.75 : 0,
                    display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center',
                    borderRadius: 99,
                    bgcolor: isToday ? 'primary.main' : 'transparent',
                    color: isToday ? 'primary.contrastText' : 'text.primary',
                    fontSize: cfg.badgeFont,
                    fontWeight: isToday ? 700 : 500,
                    letterSpacing: '-0.005em',
                  }}
                >
                  {d.getDate()}
                </Box>
                {cfg.showCount && items.length > 0 && (
                  <Typography sx={{ fontSize: 10.5, color: 'text.secondary', fontWeight: 600 }}>
                    {items.length}
                  </Typography>
                )}
              </Stack>

              {/* Priority density bars — Month/3-Month only, there's no
                  room for them at Year-view cell sizes. */}
              {cfg.showBars && items.length > 0 && (
                <Stack direction="row" spacing={0.375} sx={{ mt: 'auto', pt: 0.6 }}>
                  {counts.map((c) => (
                    <Box
                      key={c.priority}
                      sx={{
                        height: size === 'lg' ? 4 : 3,
                        flex: c.count,
                        borderRadius: 99,
                        bgcolor: (PRIORITY_META[c.priority] ?? { color: '#D97706' }).color,
                      }}
                    />
                  ))}
                </Stack>
              )}

              {/* Single density dot, colored by the highest-priority item
                  that day — Year view's stand-in for the bar row above. */}
              {cfg.showDot && items.length > 0 && (
                <Box
                  sx={{
                    width: 4, height: 4, borderRadius: 99, mt: 0.3,
                    bgcolor: (PRIORITY_META[topPriority ?? 'medium'] ?? { color: '#D97706' }).color,
                  }}
                />
              )}
            </Box>
          )

          // Compact sizes have no room to show the item count inline, so
          // a hover tooltip carries that context instead of losing it.
          if (size !== 'lg' && items.length > 0) {
            return (
              <Tooltip key={d.toISOString()} title={`${items.length} ${noun}${items.length === 1 ? '' : 's'}`} arrow>
                {cell}
              </Tooltip>
            )
          }
          return <Box key={d.toISOString()}>{cell}</Box>
        })}
      </Box>
    </Box>
  )
}

// ─── Agenda item ────────────────────────────────────────────────────────────

function AgendaItem({ wo, onClick, onContextMenu }: {
  wo: DummyWorkOrder
  onClick: () => void
  onContextMenu?: (e: React.MouseEvent) => void
}) {
  const p = PRIORITY_META[wo.priority] ?? { label: 'Medium', color: '#D97706', bg: '#FFFBEB' }
  const overdue = wo.dueDate.getTime() < Date.now() && wo.status !== 'closed' && wo.status !== 'resolved'

  return (
    <Box
      onClick={onClick}
      onContextMenu={onContextMenu}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        px: 1.5, py: 1.25,
        pl: 2,
        borderRadius: 1.5,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          borderColor: 'rgba(0,0,0,0.14)',
          boxShadow: '0 2px 10px -6px rgba(15,23,42,0.14)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0, top: 8, bottom: 8,
          width: 3,
          borderRadius: 99,
          bgcolor: p.color,
        },
      }}
    >
      <Typography
        sx={{
          fontSize: 10.5, fontWeight: 700,
          letterSpacing: '0.03em',
          color: p.color,
          textTransform: 'uppercase',
          mb: 0.25,
        }}
      >
        {p.label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13, fontWeight: 600,
          letterSpacing: '-0.005em', lineHeight: 1.3,
          mb: 0.5,
        }}
      >
        {wo.title}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Typography sx={{ fontSize: 11, color: 'text.secondary', fontFamily: 'ui-monospace, monospace' }}>
          {wo.id}
        </Typography>
        {overdue && (
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <ScheduleOutlinedIcon sx={{ fontSize: 12, color: '#DC2626' }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#DC2626' }}>
              Overdue
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildMonthGrid(anchor: Date): Date[] {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
  const start = new Date(first)
  start.setDate(first.getDate() - first.getDay())
  const days: Date[] = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    days.push(d)
  }
  return days
}

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function countByPriority(items: DummyWorkOrder[]) {
  return PRIORITY_ORDER
    .map((priority) => ({
      priority,
      count: items.filter((w) => w.priority === priority).length,
    }))
    .filter((x) => x.count > 0)
}

function priorityRank(p: DummyWorkOrder['priority']): number {
  return { critical: 4, high: 3, medium: 2, low: 1 }[p]
}
